import { ReactNode } from "react";
import {
  ButtonProps,
  Circle,
  Heading,
  SizableText,
  Token,
  XStack,
  YStack,
} from "tamagui";
import { getThemedIcon } from "tamagui-config";

import {
  GradientCard,
  GradientCardProps,
  GradientCardThemeProps,
} from "./GradientCard";

export type KPICardThemeProps = Pick<
  GradientCardThemeProps,
  "error" | "info" | "success" | "warning" | "neutral"
>;

export interface KPICardProps extends GradientCardProps {
  icon?: ButtonProps["icon"];
  iconCircleSize?: number;
  title: ReactNode;
  description?: ReactNode;
  isSelected?: boolean;
}

export const KPICard = ({
  error,
  info,
  success,
  warning,
  neutral,
  icon,
  iconCircleSize = 56,
  title,
  description,
  isSelected,
  ...rest
}: KPICardProps) => {
  const { iconBackground, iconColor } = getThemeTokens({
    error,
    info,
    success,
    warning,
    neutral,
  });

  const themedIcon = icon
    ? getThemedIcon(icon, {
        size: "$2.5",
        color: iconColor,
      })
    : undefined;

  return (
    <GradientCard
      error={error}
      info={info}
      success={success}
      warning={warning}
      neutral={neutral}
      isSelected={isSelected}
      flex={1}
      {...rest}
    >
      <XStack gap="$3.5" alignItems="center">
        {themedIcon && (
          <Circle size={iconCircleSize} backgroundColor={iconBackground}>
            {themedIcon}
          </Circle>
        )}
        <YStack gap="$1.5" flex={1} justifyContent="center">
          {typeof title === "string" || typeof title === "number" ? (
            <Heading
              fontSize="$4"
              lineHeight="$3.5"
              color="$white12"
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {title}
            </Heading>
          ) : (
            title
          )}
          {typeof description === "string" ||
          typeof description === "number" ? (
            <SizableText
              size="$3.5"
              color="$white10"
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {description}
            </SizableText>
          ) : (
            description
          )}
        </YStack>
      </XStack>
    </GradientCard>
  );
};

const getThemeTokens = ({
  error,
  info,
  success,
  warning,
  neutral,
}: KPICardThemeProps): {
  iconBackground: Token;
  iconColor: Token;
} => {
  if (error) {
    return {
      iconBackground: "$error" as Token,
      iconColor: "$red3" as Token,
    };
  }

  if (info) {
    return {
      iconBackground: "$info" as Token,
      iconColor: "$blue3" as Token,
    };
  }

  if (success) {
    return {
      iconBackground: "$successGreen" as Token,
      iconColor: "$green3" as Token,
    };
  }

  if (warning) {
    return {
      iconBackground: "$warning" as Token,
      iconColor: "$red3" as Token,
    };
  }

  if (neutral) {
    return {
      iconBackground: "$neutral" as Token,
      iconColor: "$white12" as Token,
    };
  }

  return {
    iconBackground: "$transparentBorder" as Token,
    iconColor: "$white12" as Token,
  };
};
